import { Sun, Moon, Eye, Monitor } from 'lucide-react';
import { useTheme, type ThemeChoice } from '../../hooks/useTheme';

/**
 * Theme toggle for the TopBar. Four-segment control: system / light /
 * dark / colour-blind. Picking 'system' clears the explicit override
 * so the OS preference takes over again.
 *
 * Visible by default from PR 2 onwards. The app's chrome and shared
 * primitives have all been migrated to tokens at this point, so dark
 * mode looks right on every screen that uses Card / Pill / Button.
 * Routes that haven't been individually polished yet (PR 3-4) still
 * render legibly, just without the page-specific hero treatments.
 */
export function ThemeToggle() {
  const { choice, setChoice } = useTheme();

  const options: Array<{
    value: ThemeChoice;
    label: string;
    icon: React.ReactNode;
  }> = [
    { value: 'system', label: 'System', icon: <Monitor className="h-3.5 w-3.5" /> },
    { value: 'light', label: 'Light', icon: <Sun className="h-3.5 w-3.5" /> },
    { value: 'dark', label: 'Dark', icon: <Moon className="h-3.5 w-3.5" /> },
    { value: 'cb', label: 'Colour-blind', icon: <Eye className="h-3.5 w-3.5" /> },
  ];

  return (
    <div
      role="group"
      aria-label="Theme"
      className="hidden h-9 items-center gap-0.5 rounded-token-sm border border-line-strong/15 bg-surface p-0.5 md:inline-flex"
    >
      {options.map((opt) => {
        const selected = opt.value === choice;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            title={opt.label}
            aria-label={opt.label}
            onClick={() => setChoice(opt.value)}
            className={[
              'inline-flex h-7 items-center gap-1 rounded-token-sm px-1.5 text-[11px] font-semibold transition-colors',
              selected
                ? 'bg-accent text-fg-on-accent shadow-token-sm'
                : 'text-fg-muted hover:bg-surface-2',
            ].join(' ')}
          >
            {opt.icon}
            <span className="hidden lg:inline">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
